/**
 * Prompts for event title generation / shortening.
 *
 * Title *improvement* is served by `textImprover` with `field: 'title'`; this
 * module covers generating a title from event facts.
 */

export interface EventTitlePromptInput {
  language: string;
  category?: string;
  location?: string;
  description?: string;
  tone?: string;
}

export const EVENT_TITLE_LIMITS = { maxChars: 90, maxOutputTokens: 200 };

export function eventTitleSystemPrompt({ language, tone }: EventTitlePromptInput): string {
  return `You name events for a public ticketing platform. ${
    tone ? `Tone: ${tone}. ` : ''
  }Produce a single title under ${EVENT_TITLE_LIMITS.maxChars} characters. No ALL CAPS, emojis, quotes or ending punctuation. Never invent dates, prices or performers. Respond only in ${language}.`;
}

export function eventTitleUserPrompt(input: EventTitlePromptInput): string {
  return [
    input.category ? `Category: ${input.category}` : '',
    input.location ? `Location: ${input.location}` : '',
    input.description ? `Description:\n${input.description}` : '',
  ]
    .filter(Boolean)
    .join('\n');
}

/* ------------------------------------------------------------------ */
/* Phase 2E — multi-suggestion title generation                        */
/* ------------------------------------------------------------------ */

export interface EventTitleSuggestionsPromptInput extends EventTitlePromptInput {
  audience?: string;
  keywords?: string[];
  count: number;
}

export const EVENT_TITLE_SUGGESTION_LIMITS = {
  maxChars: EVENT_TITLE_LIMITS.maxChars,
  minCount: 3,
  maxCount: 5,
  /** Enough headroom for five titles plus JSON scaffolding. */
  maxOutputTokens: 400,
};

export function eventTitleSuggestionsSystemPrompt({
  language,
  tone,
  count,
}: EventTitleSuggestionsPromptInput): string {
  return [
    'You name real events for Ticket Swiftly, a public ticket marketplace.',
    tone ? `Tone: ${tone}.` : '',
    `Return exactly ${count} distinct title options.`,
    `Each title must be under ${EVENT_TITLE_SUGGESTION_LIMITS.maxChars} characters, professional and engaging.`,
    'Do not use clickbait, hype, superlatives, ALL CAPS, emojis, quotes, exclamation marks or trailing punctuation.',
    'Never invent dates, prices, performers, venues or claims that are not in the supplied facts.',
    'Preserve the spelling of names, brands and terminology exactly as supplied.',
    'No two suggestions may be near-duplicates of each other.',
    `Write every title in ${language}.`,
    'Respond with JSON only: {"titles": ["...", "..."]}. No prose, no markdown.',
  ]
    .filter(Boolean)
    .join(' ');
}

export function eventTitleSuggestionsUserPrompt(
  input: EventTitleSuggestionsPromptInput,
): string {
  return [
    input.category ? `Category: ${input.category}` : '',
    input.location ? `Location: ${input.location}` : '',
    input.audience ? `Target audience: ${input.audience}` : '',
    input.keywords?.length ? `Keywords / theme: ${input.keywords.join(', ')}` : '',
    input.description ? `About the event:\n${input.description}` : '',
  ]
    .filter(Boolean)
    .join('\n');
}
